import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Formation {
  id: string;
  title: string;
  description: string;
  shortDescription?: string;
  duration: string;
  level: string;
  price: number;
  image: string;
  rating: number;
  studentsCount: number;
  category: string;
  source?: string;
}

const FALLBACK_FORMATIONS: Formation[] = [
  {
    id: '1',
    title: 'Airtable Débutant',
    description: 'Apprenez les bases d\'Airtable pour organiser vos données',
    shortDescription: 'Maîtrisez Airtable de A à Z',
    duration: '4 heures',
    level: 'Débutant',
    price: 99,
    image: '/placeholder.svg',
    rating: 4.8,
    studentsCount: 234,
    category: 'Airtable'
  },
  {
    id: '2',
    title: 'Zapier Avancé',
    description: 'Automatisez vos workflows avec Zapier',
    shortDescription: 'Automatisation avancée avec Zapier',
    duration: '6 heures',
    level: 'Avancé',
    price: 149,
    image: '/placeholder.svg',
    rating: 4.9,
    studentsCount: 156,
    category: 'Zapier'
  }
];

export function useFormations(options?: { silent?: boolean }) {
  const { toast } = useToast();

  const { data: formations = [], isLoading, error, refetch } = useQuery({
    queryKey: ['formations'],
    staleTime: 1000 * 60 * 30, // 30 minutes
    queryFn: async () => {
      console.log('Fetching formations from edge function...');
      try {
        const { data, error } = await supabase.functions.invoke('get-formations');

        if (error) throw error;

        if (data?.success && data?.formations) {
          return data.formations.map((formation: any) => ({
            ...formation,
            shortDescription: formation.description 
              ? formation.description.substring(0, 150) + '...' 
              : 'Formation professionnelle'
          })) as Formation[];
        }
        
        throw new Error('Format de réponse invalide');
      } catch (err: any) {
        console.error('Error fetching formations:', err);
        
        if (!options?.silent) {
          toast({
            title: "Mode hors ligne",
            description: "Chargement des données locales. " + (err.message || ""),
            variant: "default"
          });
        }
        
        return FALLBACK_FORMATIONS;
      }
    }
  });

  return {
    formations,
    loading: isLoading,
    error,
    refreshFormations: refetch
  };
}